import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useStore } from '../../contexts/StoreContext';
import AuthGate from '../../components/AuthGate/AuthGate';
import authService from '../../services/auth.service';
import Input from '../../components/Input/Input';
import Button from '../../components/Button/Button';
import { useThemeColor } from '../../hooks/useThemeColor';

const RESEND_COOLDOWN_SECONDS = 60;

/** Map API error codes to French messages */
const API_ERROR_MESSAGES: Record<string, string> = {
  PHONE_ALREADY_EXISTS: 'Ce numéro de téléphone est déjà enregistré.',
  EMAIL_ALREADY_EXISTS: 'Cette adresse e-mail est déjà utilisée.',
  PSEUDO_ALREADY_EXISTS: 'Ce pseudo est déjà utilisé.',
  INVALID_OR_EXPIRED_OTP: 'Code invalide ou expiré. Demandez un nouveau code.',
  SMS_SEND_FAILED: 'Impossible d\'envoyer le code. Réessayez plus tard.',
  VALIDATION_ERROR: 'Vérifiez les champs du formulaire.',
};

function getApiErrorMessage(err: any): string {
  const code = err.response?.data?.error?.code;
  const fallback = err.response?.data?.error?.message || err.message;
  return (code && API_ERROR_MESSAGES[code]) || fallback;
}

/** Map known English API messages to French (when error comes as string from context) */
function translateErrorMessage(message: string | null): string {
  if (!message) return '';
  if (message.includes('phone number is already')) return API_ERROR_MESSAGES.PHONE_ALREADY_EXISTS;
  if (message.includes('email address is already')) return API_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS;
  if (message.includes('pseudo is already')) return API_ERROR_MESSAGES.PSEUDO_ALREADY_EXISTS;
  if (message.includes('Invalid or expired')) return API_ERROR_MESSAGES.INVALID_OR_EXPIRED_OTP;
  return message;
}

export interface RegisterVerifyLocationState {
  formData: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    phone: string;
    pseudo: string;
  };
  phoneNormalized: string;
  smsNotDelivered?: boolean;
}

/**
 * Mask phone for display: show last 4 digits, e.g. *** *** 2425
 */
function maskPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return phone;
  return '*** *** ' + digits.slice(-4);
}

const RegisterVerify: React.FC = () => {
  useThemeColor('#6B21A8');
  const navigate = useNavigate();
  const location = useLocation();
  const { register, isLoading, error, clearError } = useAuth();
  const { clearCurrentStore } = useStore();

  const state = location.state as RegisterVerifyLocationState | null;
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState('');
  const [smsNotDelivered, setSmsNotDelivered] = useState(Boolean(state?.smsNotDelivered));
  const [cooldownLeft, setCooldownLeft] = useState(RESEND_COOLDOWN_SECONDS);

  // Redirect if no state (user came directly to this URL)
  useEffect(() => {
    if (!state?.formData || !state?.phoneNormalized) {
      navigate('/register', { replace: true });
    }
  }, [state, navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (cooldownLeft <= 0) return;
    const t = setInterval(() => setCooldownLeft((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [cooldownLeft]);

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(v);
    if (otpError) setOtpError('');
    if (error) clearError();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!state?.formData || !state?.phoneNormalized) return;
    if (otp.length !== 6) {
      setOtpError('Saisissez le code à 6 chiffres');
      return;
    }
    setOtpError('');
    try {
      await register({
        ...state.formData,
        phone: state.phoneNormalized,
        otp,
      });
      clearCurrentStore();
      navigate('/stores', { replace: true });
    } catch (err) {
      console.error('Registration error:', err);
    }
  };

  const handleResend = async () => {
    if (!state?.phoneNormalized || cooldownLeft > 0) return;
    setResendError('');
    setResendLoading(true);
    try {
      const otpResponse = await authService.sendOtp(state.phoneNormalized);
      setSmsNotDelivered(otpResponse.data?.smsDelivered === false);
      setCooldownLeft(RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      setResendError(getApiErrorMessage(err) || 'Impossible de renvoyer le code.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleChangeNumber = () => {
    if (!state?.formData) return;
    navigate('/register', {
      state: { previousFormData: { ...state.formData, confirmPassword: state.formData.password } },
      replace: false,
    });
  };

  if (!state?.formData || !state?.phoneNormalized) {
    return null; // redirecting
  }

  return (
    <AuthGate
      panel={{
        eyebrow: 'Inscription',
        title: <>Le code <em>SMS.</em></>,
        lead: '6 chiffres, puis le store.',
      }}
    >
      <p className="auth-kicker">Vérification</p>
      <h1 className="auth-h">Vérifiez le numéro</h1>
      <p className="auth-sub">
        {smsNotDelivered
          ? `Code pour le ${maskPhone(state.phoneNormalized)}`
          : `Envoyé au ${maskPhone(state.phoneNormalized)}`}
      </p>
      <ol className="auth-steps">
        <li>Infos</li>
        <li className="is-on">Code</li>
      </ol>

      {smsNotDelivered && (
        <div className="auth-alert auth-alert--warn" role="status">
          SMS non envoyé. Code dans les logs serveur.
        </div>
      )}

      {(error || resendError) && (
        <div className="auth-alert auth-alert--error" role="alert">
          {translateErrorMessage(error) || resendError}
        </div>
      )}

      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <Input
          label="Code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={otp}
          onChange={handleOtpChange}
          error={otpError}
          placeholder="000000"
          maxLength={6}
          fullWidth
          autoFocus
        />

        <Button
          type="submit"
          variant="primary"
          size="large"
          fullWidth
          loading={isLoading}
          disabled={otp.length !== 6}
        >
          Vérifier
        </Button>

        <div className="auth-verify-actions">
          <button
            type="button"
            className="auth-link"
            onClick={handleResend}
            disabled={cooldownLeft > 0 || resendLoading}
          >
            {cooldownLeft > 0
              ? `Renvoyer dans ${cooldownLeft} s`
              : resendLoading
                ? 'Envoi…'
                : 'Renvoyer le code'}
          </button>
          <button
            type="button"
            className="auth-link"
            onClick={handleChangeNumber}
          >
            Changer de numéro
          </button>
        </div>
      </form>

      <div className="auth-foot">
        <p>
          <Link to="/register">Retour à l’inscription</Link>
        </p>
      </div>
    </AuthGate>
  );
};

export default RegisterVerify;
